import { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '@perch/ui-tokens';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { buttonVariants, SectionLabel, StatusDot } from '@perch/ui';
import { RouteDisplay } from '../ui/RouteDisplay';

function randomFutureDate(): string {
  const days = 14 + Math.floor(Math.random() * 77); // 14–90 days out
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface HeroSectionProps {
  heroContentRef: React.RefObject<HTMLDivElement | null>;
  isVisible: boolean;
}

export function HeroSection({
  heroContentRef,
  isVisible,
}: HeroSectionProps) {
  const reduced = useReducedMotion();
  const flightDate = useMemo(randomFutureDate, []);

  return (
    <section
      className="relative min-h-screen flex items-center justify-center pt-20 pb-32 px-4 sm:px-6 overflow-hidden"
    >
      {/* Flight path arcs — subtle dotted curves replacing generic gradient blobs */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 1200 800"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <path
          d="M-100,650 Q400,150 750,300 T1300,200"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeDasharray="4 12"
          className="text-stone-300/40 dark:text-stone-600/30"
        />
        <path
          d="M-50,750 Q500,250 900,400 T1350,150"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeDasharray="3 10"
          className="text-stone-300/25 dark:text-stone-600/20"
        />
        {/* Small plane icon on the first path */}
        <circle cx="750" cy="300" r="2" className="fill-stone-300 dark:fill-stone-600" />
      </svg>

      <div
        ref={heroContentRef}
        className="relative z-10 max-w-4xl mx-auto text-center"
        style={{}}
      >
        {/* Price drop notification — previews what Perch actually does */}
        <div
          className={`inline-block mb-10 transition-[opacity,transform] duration-500 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ transitionDelay: '100ms' }}
        >
          <div className="bg-white dark:bg-stone-900 rounded-2xl px-5 py-4 shadow-lg shadow-stone-900/[0.04] dark:shadow-black/20 border border-stone-200/60 dark:border-stone-700/50 text-left">
            <div className="flex items-center gap-2 mb-2.5">
              <StatusDot />
              <span className="text-[10px] uppercase tracking-widest text-emerald-600 dark:text-emerald-400 font-medium">Price Drop Found</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="min-w-0">
                <RouteDisplay segments={[{ origin: 'SFO', destination: 'JFK' }]} size="sm" />
                <div className="text-xs text-stone-400 dark:text-stone-500">United 237 · {flightDate}</div>
              </div>
              <div className="border-l border-stone-200/60 dark:border-stone-700/50 pl-4">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-stone-400 dark:text-stone-500 line-through text-sm">$412</span>
                  <span className="font-data tabular-nums text-lg text-stone-900 dark:text-stone-100">$285</span>
                </div>
                <div className="text-emerald-600 dark:text-emerald-400 text-xs font-medium">$127 saved</div>
              </div>
            </div>
          </div>
        </div>

        {/* Main headline */}
        <h1
          className={`text-5xl sm:text-6xl md:text-7xl font-serif font-light tracking-[-0.03em] text-stone-900 dark:text-stone-100 leading-[1.1] mb-6 transition-[opacity,transform] duration-500 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '200ms' }}
        >
          <span className="text-[#FF6B35]">Prices Drop</span>
          <br />
          <span className="text-stone-400 dark:text-stone-500">After You Book.</span>
        </h1>

        {/* Subheadline */}
        <p
          className={`text-lg sm:text-xl text-stone-500 dark:text-stone-400 max-w-2xl mx-auto mb-12 leading-relaxed transition-[opacity,transform] duration-500 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '300ms' }}
        >
          We handle the rebook. You pocket the difference.
        </p>

        {/* CTA + stat — consolidated into one block */}
        <div
          className={`transition-[opacity,transform] duration-500 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '400ms' }}
        >
          <a
            href="#waitlist"
            className={cn(buttonVariants({ variant: 'coral', size: 'lg' }), 'group gap-3')}
          >
            Start Tracking Free
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </a>
          <div className="mt-6 flex items-center justify-center gap-2">
            <span className={`text-2xl font-data tabular-nums text-emerald-600 dark:text-emerald-400${isVisible && !reduced ? ' stat-flip-in' : ''}`}>$247</span>
            <span className="text-sm text-stone-400 dark:text-stone-500">avg. saved per trip</span>
          </div>
        </div>

      </div>

      {/* Scroll indicator */}
      <div className={`absolute bottom-8 inset-x-0 flex flex-col items-center gap-2 text-stone-400 dark:text-stone-500 ${!reduced ? 'animate-[gentle-bounce_3s_ease-in-out_infinite]' : ''}`}>
        <SectionLabel>Scroll</SectionLabel>
        <div className="w-px h-8 bg-gradient-to-b from-stone-300 dark:from-stone-600 to-transparent" />
      </div>
    </section>
  );
}
